/**
 * Image Lightbox Function
 *
 * @author Matt Haynes <matthew.haynes@joipolloi.com>
 * @package The University of Sheffield - Performance Venues
 * @npm https://www.npmjs.com/package/img-lightbox
 *
 * To add Lightbox to any image on the site you can add:
 * 1. data-src, matching the normal image src
 * 2. class='img-lightbox-link'
 * 3. rel='lightbox'
 * <img src='image-source' data-src='image-source' class='img-lightbox-link' rel='lightbox' />
 */

/*!
 * @see {@link https://github.com/englishextra/img-lightbox}
 * imgLightbox
 * requires this very img-lightbox.js, and animate.css, img-lightbox.css
 * @params {String} linkClass
 * @params {Object} settings object
 * imgLightbox(linkClass, settings)
 * passes jshint
 */
/* jshint -W014 */

const ImageLightBox = () => {
    (function (root, document) {
        const docElem = document.documentElement || '';
        const docBody = document.body || '';
        const animatedClass = 'animated';
        const appendChild = 'appendChild';
        const classList = 'classList';
        const createElement = 'createElement';
        const getAttribute = 'getAttribute';
        const getElementsByClassName = 'getElementsByClassName';
        const getElementsByTagName = 'getElementsByTagName';
        const innerHTML = 'innerHTML';
        const style = 'style';
        const _addEventListener = 'addEventListener';
        const _length = 'length';
        const btnCloseClass = 'btn-close';
        const containerClass = 'img-lightbox';
        const fadeInClass = 'fadeIn';
        const fadeInUpClass = 'fadeInUp';
        const fadeOutClass = 'fadeOut';
        const fadeOutDownClass = 'fadeOutDown';
        const imgLightboxOpenClass = 'img-lightbox--open';
        const imgLightboxLinkIsBindedClass = 'img-lightbox-link--is-binded';
        const isLoadedClass = 'is-loaded';
        const dummySrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
        const isMobile = navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(Android)|(PlayBook)|(BB10)|(BlackBerry)|(Opera Mini)|(IEMobile)|(webOS)|(MeeGo)/i);
        const isTouch = isMobile !== null || document.createTouch !== undefined || 'ontouchstart' in root || 'onmsgesturechange' in root || navigator.msMaxTouchPoints;
        const debounce = function (func, wait) {
            let timeout;
            let args;
            let context;
            let timestamp;
            return function () {
                context = this;
                args = [].slice.call(arguments, 0);
                timestamp = new Date();
                const later = function () {
                    const last = new Date() - timestamp;
                    if (last < wait) {
                        timeout = setTimeout(later, wait - last);
                    } else {
                        timeout = null;
                        func.apply(context, args);
                    }
                };
                if (!timeout) {
                    timeout = setTimeout(later, wait);
                }
            };
        };
        const callCallback = function (func, data) {
            if (typeof func !== 'function') {
                return;
            }
            const caller = func.bind(this);
            caller(data);
        };
        const hideImgLightbox = function (callback) {
            const container = document[getElementsByClassName](containerClass)[0] || '';
            const img = container ? container[getElementsByTagName]('img')[0] || '' : '';
            const hideContainer = function () {
                container[classList].remove(fadeInClass);
                container[classList].add(fadeOutClass);
                const hideImg = function () {
                    container[classList].remove(animatedClass);
                    container[classList].remove(fadeOutClass);
                    img[classList].remove(animatedClass);
                    img[classList].remove(fadeOutDownClass);
                    img.onload = function () {
                        container[classList].remove(isLoadedClass);
                    };
                    img.src = dummySrc;
                    container[style].display = 'none';
                    callCallback(callback, root);
                };
                let timer = setTimeout(() => {
                    clearTimeout(timer);
                    timer = null;
                    hideImg();
                }, 400);
            };
            if (container && img) {
                img[classList].remove(fadeInUpClass);
                img[classList].add(fadeOutDownClass);
                let timer = setTimeout(() => {
                    clearTimeout(timer);
                    timer = null;
                    hideContainer();
                }, 400);
            }
            docElem[classList].remove(imgLightboxOpenClass);
            docBody[classList].remove(imgLightboxOpenClass);
        };
        const imgLightbox = function (linkClass, settings) {
            const _linkClass = linkClass || '';
            const options = settings || {};
            const rate = options.rate || 500;
            const { touch } = options;
            const { onError } = options;
            const { onLoaded } = options;
            const { onCreated } = options;
            const { onClosed } = options;
            const link = document[getElementsByClassName](_linkClass) || '';
            let container = document[getElementsByClassName](containerClass)[0] || '';
            let img = container ? container[getElementsByTagName]('img')[0] || '' : '';
            if (!container) {
                container = document[createElement]('div');
                container[classList].add(containerClass);
                const html = [];
                html.push(`<img src="${dummySrc}" alt="" />`);
                html.push('<div class="half-circle-spinner"><div class="circle circle-1"></div><div class="circle circle-2"></div></div>');
                html.push('<a href="javascript:void(0);" class="btn-close"></a>');
                container[innerHTML] = html.join('');
                docBody[appendChild](container);
                img = container ? container[getElementsByTagName]('img')[0] || '' : '';
                const btnClose = container ? container[getElementsByClassName](btnCloseClass)[0] || '' : '';
                const handleImgLightboxContainer = function () {
                    hideImgLightbox(onClosed);
                };
                container[_addEventListener]('click', handleImgLightboxContainer);
                btnClose[_addEventListener]('click', handleImgLightboxContainer);
                root[_addEventListener]('keyup', ev => {
                    if ((ev.which || ev.keyCode) === 27) {
                        hideImgLightbox(onClosed);
                    }
                });
            }
            const arrange = function (e) {
                const hrefString = e[getAttribute]('href') || e[getAttribute]('data-src') || '';
                const dataTouch = e[getAttribute]('data-touch') || '';
                if (!hrefString) {
                    return;
                }
                const handleImgLightboxLink = function (ev) {
                    ev.stopPropagation();
                    ev.preventDefault();
                    docElem[classList].add(imgLightboxOpenClass);
                    docBody[classList].add(imgLightboxOpenClass);
                    container[classList].remove(isLoadedClass);
                    const logic = function logic() {
                        if (onCreated) {
                            callCallback(onCreated, root);
                        }
                        container[classList].add(animatedClass);
                        container[classList].add(fadeInClass);
                        img[classList].add(animatedClass);
                        img[classList].add(fadeInUpClass);
                        img.onload = function () {
                            container[classList].add(isLoadedClass);
                            if (onLoaded) {
                                callCallback(onLoaded, root);
                            }
                        };
                        img.onerror = function () {
                            if (onError) {
                                callCallback(onError, root);
                            }
                        };
                        img.src = hrefString;
                        container[style].display = 'block';
                    };
                    debounce(logic, rate).call();
                };
                if (!e[classList].contains(imgLightboxLinkIsBindedClass)) {
                    e[classList].add(imgLightboxLinkIsBindedClass);
                    e[_addEventListener]('click', handleImgLightboxLink);
                    if (isTouch && (touch || dataTouch)) {
                        e[_addEventListener]('touchstart', handleImgLightboxLink);
                    }
                }
            };
            if (container && img && link) {
                let i;
                let l;
                for (i = 0, l = link[_length]; i < l; i += 1) {
                    arrange(link[i]);
                }
                i = l = null;
            }
        };
        root.imgLightbox = imgLightbox;
    }(typeof window !== 'undefined' ? window : this, document));

    (function (root) {
        if (root.imgLightbox) {
            imgLightbox('img-lightbox-link', {
                onCreated() {
                    /* show your preloader */
                },
                onLoaded() {
                    /* hide your preloader */
                },
                onError() {
                    /* hide your preloader */
                },
                onClosed() {
                    /* hide your preloader */
                },
                rate: 500, /* default: 500 */
            });
        }
    }(typeof window !== 'undefined' ? window : this));
};

export default ImageLightBox;
